import { all, call, put, takeLatest, race, delay } from "redux-saga/effects";
import {
  CREATE_EMPLOYEE,
  CREATE_USER,
  CreateEmployeeAction,
  createEmployeeSuccess,
  CreateUserAction,
  createUserSuccess,
  DELETE_USER,
  DeleteUserAction,
  deleteUserSuccess,
  LOAD_ALL_USERS,
  LoadAllUsersAction,
  loadAllUsersSuccess,
  REASSIGN_EMPLOYEE_ASSIGNMENTS, ReassignEmployeeAssignmentsAction, reassignEmployeeAssignmentsSuccess, RESEND_USER_INVITE, ResendUserInviteAction, resendUserInviteSuccess,
  UPDATE_USER,
  UpdateUserAction, updateUserSuccess, clearUserManagementLoadingLabel,
  REVOKE_WITH_REASSIGNMENT, RevokeWithReassignmentAction, revokeWithReassignmentSuccess,
  REINSTATE_USER, ReinstateUserAction, reinstateUserSuccess,
  SEND_APP_CENTER_LINK, SendAppCenterLinkAction, sendAppCenterLinkSuccess,
  REQUEST_PASSWORD_RESET, RequestPasswordResetAction, requestPasswordResetSuccess,
  GENERATE_INVITATION_LINK, GenerateInvitationLinkAction, generateInvitationLinkSuccess, clearInvitationLink
} from "./actions";
import { handleResponse, httpDelete, httpGet, httpPost, httpPut } from "../../utils/HttpClient";
import { User } from "@amsanalytics/react-shared-library";
import { CreateUserResult } from "../../Data/UserSummary";
import {displayError, displayMessage} from "../../utils/utils";
import { authService } from '../../index';

function* loadAllUsersSaga(action: LoadAllUsersAction) {
  try {
    const token: string = yield authService.getToken();
    // const response = yield authService.microsoftGraphApi.getActiveDirectoryUsers(token);
    const response: { json: any, ok: boolean, status: number } = yield call(httpGet,
      `api/user/all?clientId=${action.payload.clientId}`,
      token
    );
    if (response.ok === false) {
      const error: string = yield call([response, response.json]);
      yield (put(displayError(error)));
    } else {
      const users: User[] = yield call([response, response.json]);
      yield put(loadAllUsersSuccess(users));
    }

  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}
export function* watchLoadAllUsers() {
  yield takeLatest(LOAD_ALL_USERS, loadAllUsersSaga);
}

function* createUserSaga(action: CreateUserAction) {
  try {
    const token: string = yield authService.getToken();
    const user = {
      ...action.payload.user,
      adUserId : action.payload.user.adUserId.replace('@@', '@')
    }
    const response: { json: any, status: number } = yield call(httpPost,
      `api/user`,
      user,
      token
    );
    if (response.status === 401) {
      yield put(displayError("You do not have permission to create users. For any questions regarding your permissions, please contact your administrator."));
    }
    // get list of messages from response
    const result: CreateUserResult = yield call([response, response.json]);
    yield all([
      put(createUserSuccess({ ...result, user: user })),
      put(displayMessage(result.messages.join(' '), result.success ? 'success' : 'error'))
    ]);
  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}

export function* watchCreateUser() {
  yield takeLatest(CREATE_USER, createUserSaga);
}

function* createEmployeeSaga(action: CreateEmployeeAction) {
  try {
    const token: string = yield authService.getToken();
    const employee = {
      ...action.payload.employee,
      adUserId : action.payload.employee.adUserId.replace('@@', '@')
    }
    const response: { json: any, status: number } = yield call(httpPost,
      `api/user`,
      employee,
      token
    );
    if (response.status === 401) {
      yield put(displayError("You do not have permission to create employees. For any questions regarding your permissions, please contact your administrator."));
    }
    // get list of messages from response
    const result: Omit<CreateUserResult, 'user'> = yield call([response, response.json]);
    if (result.success) {
      yield put(createEmployeeSuccess({ ...result, user: employee }));
    }
    yield put(displayMessage(result.messages.join(' '), result.success ? 'success' : 'error'));
  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}

export function* watchCreateEmployee() {
  yield takeLatest(CREATE_EMPLOYEE, createEmployeeSaga);
}

function* updateUserSaga(action: UpdateUserAction) {
  try {
    const token: string = yield authService.getToken();
    const response: { json: any, status: number } = yield call(httpPut,
      `api/user`,
      action.payload.user,
      token
    );
    // 400 indicates invalid user data. We want to display
    // validation errors to the user.
    if (response.status === 400) {
      const errorJson: { errors: { [key: string]: string[] } } = yield call([response, response.json]);
      let errorString: string = 'Could not update user! ';
      // Collect all errors in one string to display to user.
      for (const key of Object.keys(errorJson.errors)) {
        errorString += errorJson.errors[key] + '. ';
      }
      yield put(displayError(errorString));
    } else if (response.status === 500) {
      // 500 status indicates a server error.
      yield put(displayError(`We ran into an issue while updating the account for ${action.payload.user.adUserId}. Please try again.`));
    } else {
      const user: User = yield call([response, response.json]);
      yield put(updateUserSuccess(user));
    }
  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}

export function* watchUpdateUser() {
  yield takeLatest(UPDATE_USER, updateUserSaga);
}

function* resendUserInvite(action: ResendUserInviteAction) {
  try {
    const token: string = yield authService.getToken();
    const encoded = encodeURIComponent(action.payload.adUserId);
    // Step 1: resend the Graph invitation (guest-only endpoint).
    const response: { status: number, json: any } = yield call(httpPost,
      `api/user/${encoded}/resend-invitation`,
      {},
      token
    );
    if (response.status !== 200) {
      const result: string = yield call(handleResponse, response);
      yield put(displayError(result || 'Failed to resend invitation.'));
      return;
    }
    // Step 2 (inactive guests only): reactivate via the activation-only endpoint — NOT the legacy resendInvite route.
    if (action.payload.reactivateAfterResend) {
      const reinstateResponse: { status: number, json: any } = yield call(httpPost,
        `api/user/${encoded}/reinstate`,
        {},
        token
      );
      if (reinstateResponse.status !== 200) {
        yield put(displayError('Invitation resent, but reactivation failed — please contact support.'));
        return;
      }
    }
    yield put(resendUserInviteSuccess(action.payload.adUserId));
    yield put(displayMessage('Successfully resent invitation.', 'success'));
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchResendUserInvite() {
  yield takeLatest(RESEND_USER_INVITE, resendUserInvite);
}

function* reinstateUser(action: ReinstateUserAction) {
  try {
    const token: string = yield authService.getToken();
    const encoded = encodeURIComponent(action.payload.adUserId);
    const response: { status: number, json: any } = yield call(httpPost,
      `api/user/${encoded}/reinstate`,
      {},
      token
    );
    if (response.status === 200) {
      yield put(reinstateUserSuccess(action.payload.adUserId));
      yield put(displayMessage('User reinstated.', 'success'));
    } else {
      const result: string = yield call(handleResponse, response);
      yield put(displayError(result || 'Failed to reinstate user.'));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchReinstateUser() {
  yield takeLatest(REINSTATE_USER, reinstateUser);
}

function* sendAppCenterLink(action: SendAppCenterLinkAction) {
  try {
    const token: string = yield authService.getToken();
    const encoded = encodeURIComponent(action.payload.adUserId);
    const response: { status: number, json: any } = yield call(httpPost,
      `api/user/${encoded}/send-app-center-link`,
      {},
      token
    );
    if (response.status === 200) {
      yield put(sendAppCenterLinkSuccess(action.payload.adUserId));
      yield put(displayMessage('Sent App Center link.', 'success'));
    } else {
      const result: string = yield call(handleResponse, response);
      yield put(displayError(result || 'Failed to send App Center link.'));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchSendAppCenterLink() {
  yield takeLatest(SEND_APP_CENTER_LINK, sendAppCenterLink);
}

function* requestPasswordReset(action: RequestPasswordResetAction) {
  try {
    const token: string = yield authService.getToken();
    const encoded = encodeURIComponent(action.payload.adUserId);
    const response: { status: number, json: any } = yield call(httpPost,
      `api/user/${encoded}/request-password-reset`,
      {},
      token
    );
    if (response.status === 200) {
      yield put(requestPasswordResetSuccess(action.payload.adUserId));
      yield put(displayMessage('Password reset request sent to Auto Master Systems.', 'success'));
    } else {
      const result: string = yield call(handleResponse, response);
      yield put(displayError(result || 'Failed to send password reset request.'));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchRequestPasswordReset() {
  yield takeLatest(REQUEST_PASSWORD_RESET, requestPasswordReset);
}

function* generateInvitationLink(action: GenerateInvitationLinkAction) {
  try {
    const token: string = yield authService.getToken();
    const encoded = encodeURIComponent(action.payload.adUserId);
    // Mints a fresh redeem link with SendInvitationMessage = false (no email). The URL is returned to the UI for the
    // admin to copy and deliver; it is never logged. Race against a 10s timeout so the loading dialog can't hang
    // forever if the Graph call stalls.
    const { response, timeout }: { response?: { status: number, json: any }, timeout?: boolean } = yield race({
      response: call(httpPost,
        `api/user/${encoded}/invitation-link`,
        {},
        token
      ),
      timeout: delay(10000),
    });
    if (timeout || !response) {
      yield put(clearInvitationLink());
      yield put(displayError('Generating the invitation link timed out. Please try again.'));
      return;
    }
    const result: { url?: string } = yield call(handleResponse, response);
    if (response.status === 200 && result?.url) {
      yield put(generateInvitationLinkSuccess(result.url));
    } else {
      // Clear the in-flight state so the loading dialog closes; the error toast carries the message.
      yield put(clearInvitationLink());
      yield put(displayError('Failed to generate an invitation link.'));
    }
  } catch (error) {
    yield put(clearInvitationLink());
    yield put(displayError(error as string));
  }
}

export function* watchGenerateInvitationLink() {
  yield takeLatest(GENERATE_INVITATION_LINK, generateInvitationLink);
}

function* deleteUser(action: DeleteUserAction) {
  try {
    const token: string = yield authService.getToken();
    const response: { status: number, json: any } = yield call(httpDelete,
      `api/user/${action.payload.adUserId}`,
      token
    );
    const result: { error?: string } = yield call([response, response.json]);
    if (response.status === 200) {
      yield put(deleteUserSuccess(action.payload.adUserId));
    } else {
      if (result.error) {
        yield all([
          put(displayError(result.error)),
          put(clearUserManagementLoadingLabel())
        ]);
      }
    }
  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}

export function* watchDeleteUser() {
  yield takeLatest(DELETE_USER, deleteUser);
}

export function* reassignEmployeeAssignmentsSaga(action: ReassignEmployeeAssignmentsAction) {
  try {
    const token: string = yield authService.getToken();
    const response: { json: any } = yield call(httpPost, `api/employee/reassign`, { fromEmployeeId: action.payload.fromEmployeeId, toEmployeeIds: action.payload.toEmployeeIds }, token);
    yield put(reassignEmployeeAssignmentsSuccess(action.payload.fromEmployeeId, action.payload.toEmployeeIds));
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchReassignEmployeeAssignments() {
  yield takeLatest(REASSIGN_EMPLOYEE_ASSIGNMENTS, reassignEmployeeAssignmentsSaga);
}

// Revoke With Reassignment (atomic)
function* revokeWithReassignmentSaga(action: RevokeWithReassignmentAction) {
  try {
    const token: string = yield authService.getToken();
    const response: { json: any; ok: boolean; status: number } = yield call(
      httpPost,
      `api/employee/revokeWithReassignment`,
      {
        adUserId: action.payload.adUserId,
        fromEmployeeId: action.payload.fromEmployeeId,
        toEmployeeIds: action.payload.toEmployeeIds
      },
      token
    );
    if (response.ok) {
      yield put(revokeWithReassignmentSuccess(action.payload.adUserId));
      yield put(displayMessage('User revoked and contacts reassigned successfully.', 'success'));
    } else {
      yield put(displayError('Failed to revoke user.'));
      yield put(clearUserManagementLoadingLabel());
    }
  } catch (error) {
    yield all([
      put(displayError(error as string)),
      put(clearUserManagementLoadingLabel())
    ]);
  }
}

export function* watchRevokeWithReassignment() {
  yield takeLatest(REVOKE_WITH_REASSIGNMENT, revokeWithReassignmentSaga);
}

export default function* adminSagas() {
  yield all([
    watchLoadAllUsers(),
    watchCreateUser(),
    watchCreateEmployee(),
    watchUpdateUser(),
    watchResendUserInvite(),
    watchReinstateUser(),
    watchSendAppCenterLink(),
    watchRequestPasswordReset(),
    watchGenerateInvitationLink(),
    watchDeleteUser(),
    watchReassignEmployeeAssignments(),
    watchRevokeWithReassignment()
  ])
}
